import dayjs from "dayjs"
import useCurrentBrand from "Hooks/useCurrentBrand"
import Colors from "modules/Colors"
import Sty from "modules/Sty"
import { styx } from "modules/Utils"
import { clsx } from "modules/Utils"
import { CSSProperties, useEffect, useState } from "react"

interface MessageProps {
    className: string
    style?: CSSProperties
    text: string
    isUser: boolean
    createdAt: string
    isStreaming?: boolean
}

const Message = ({
    className,
    style,
    text,
    isUser,
    createdAt,
    isStreaming = false,
}: MessageProps) => {
    const {
        getBrandImage
    } = useCurrentBrand()

    // Typing animation dots for streaming effect
    const [typingDots, setTypingDots] = useState('');

    // Update typing dots animation when streaming
    useEffect(() => {
        if (!isStreaming) return;
        
        const intervalId = setInterval(() => {
            setTypingDots(prev => {
                if (prev === '') return '.';
                if (prev === '.') return '..';
                if (prev === '..') return '...';
                return '';
            });
        }, 500);
        
        return () => clearInterval(intervalId);
    }, [isStreaming]);

    const messageElement = (
        <div
            className={clsx('messageElement', className, isStreaming ? 'streaming' : '')}
            style={styx(
                sty.messageElement,
                isUser ? sty.messageUser : sty.messageMaestro,
                isStreaming ? sty.messageStreaming : {},
                style
            )}
        >
            <div className="userLabel" style={styx(
                sty.userLabel,
                isUser ? sty.userLabelUser : sty.userLabelMaestro
            )}>
                {isUser ? 'You' : 'Maestro AI'}
            </div>
            <div
                className="messageText"
                style={sty.messageText}
            >
                {text}
                {isStreaming && (
                    <span className="typingIndicator" style={sty.typingIndicator}>
                        {typingDots}
                    </span>
                )}
            </div>
        </div>
    )

    return (
        <div
            className="messageOuterContainer"
            style={styx(
                sty.messageOuterContainer,
                isUser ? sty.messageOuterContainerUser : sty.messageOuterContainerMaestro
            )}
        >
            <div
                className="messageContainer"
                style={sty.messageContainer}
            >
                {messageElement}
                {isUser &&
                    <img
                        src={getBrandImage()}
                        alt="brand"
                        style={sty.messageImage}
                    />
                }
            </div>
            <div
                className="messageCreatedAt"
                style={styx(
                    sty.messageCreatedAt,
                    isUser ? sty.messageCreatedAtUser : sty.messageCreatedAtMaestro
                )}
            >
                {isStreaming ? 'typing...' : dayjs(createdAt).format('hh:mm A')}
            </div>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    messageElement: {
        display: 'flex',
        flexDirection: 'column',
        padding: 12,
        ...Sty.text14
    },
    userLabel: {
        ...Sty.text12grey,
        marginBottom: -2,
    },
    userLabelUser: {
        marginLeft: 'auto',
    },
    userLabelMaestro: {
        marginRight: 'auto',
    },
    messageUser: {
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderBottomLeftRadius: 16,
        borderBottomRightRadius: 0,
        backgroundColor: Colors.greyLight,
    },
    messageMaestro: {
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 16,
        backgroundColor: Colors.purpleTransparent,
    },
    messageStreaming: {
        borderBottomRightRadius: 16,
        borderBottomLeftRadius: 0,
        animation: 'pulse 2s infinite ease-in-out',
    },
    messageImage: {
        width: 24,
        height: 24,
        borderRadius: 100,
    },
    messageContainer: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 4,
    },
    messageOuterContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '80%',
    },
    messageOuterContainerUser: {
        marginLeft: 'auto',
        alignItems: 'flex-end',
    },
    messageOuterContainerMaestro: {
    },
    messageCreatedAt: {
        ...Sty.text12grey,
    },
    messageCreatedAtUser: {
        marginLeft: 'auto',
        marginRight: 28,
    },
    messageCreatedAtMaestro: {
        marginRight: 'auto',
    },
    typingIndicator: {
        display: 'inline-block',
        width: 20,
        fontWeight: 'bold',
        marginLeft: 4,
    },
    messageText: {
        position: 'relative',
    }
}

export default Message;